import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import ChangeRequestTaskField from '../../../components/lookup/ChangeRequestTaskField';
import { getCabMeetingById } from '../../../services/cabMeetingsService';
import {
  deleteCabAgendaItem,
  getCabAgendaItemById,
  updateCabAgendaItem,
} from '../../../services/cabAgendaItemsService';
import {
  CAB_AGENDA_SHORT_DESCRIPTION_MAX,
  CAB_AGENDA_STATUS_OPTIONS,
} from '../../../constants/cabAgendaOptions';
import {
  agendaItemToFormData,
  hasAgendaValidationErrors,
  validateCabAgendaItemForm,
  type CabAgendaItemFormErrors,
} from '../../../utils/cabAgendaValidation';
import type { CabAgendaItemFormData, LookupChangeRequest } from '../../../types/cabAgendaItem';

const CabAgendaItemEdit: React.FC = () => {
  const { meetingId, agendaItemId } = useParams<{
    meetingId: string;
    agendaItemId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<CabAgendaItemFormData | null>(null);
  const [errors, setErrors] = useState<CabAgendaItemFormErrors>({});

  const { data: meeting, isLoading: meetingLoading } = useQuery({
    queryKey: ['cab-meeting', meetingId],
    queryFn: () => getCabMeetingById(meetingId!),
    enabled: Boolean(meetingId),
  });

  const {
    data: agendaItem,
    isLoading: itemLoading,
    isError: itemError,
  } = useQuery({
    queryKey: ['cab-agenda-item', meetingId, agendaItemId],
    queryFn: () => getCabAgendaItemById(meetingId!, agendaItemId!),
    enabled: Boolean(meetingId && agendaItemId),
  });

  React.useEffect(() => {
    if (agendaItem) {
      setFormData(agendaItemToFormData(agendaItem));
    }
  }, [agendaItem]);

  const saveMutation = useMutation({
    mutationFn: (payload: {
      meetingName: string;
      changeRequestId: string;
      shortDescription: string;
      status: CabAgendaItemFormData['status'];
      sortOrder: number;
    }) => updateCabAgendaItem(meetingId!, agendaItemId!, payload),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['cab-meeting-agenda', meetingId] });
      queryClient.invalidateQueries({ queryKey: ['cab-agenda-item', meetingId, agendaItemId] });
      queryClient.invalidateQueries({ queryKey: ['cab-workbench-meetings'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCabAgendaItem(meetingId!, agendaItemId!),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['cab-meeting-agenda', meetingId] });
      queryClient.removeQueries({ queryKey: ['cab-agenda-item', meetingId, agendaItemId] });
      queryClient.invalidateQueries({ queryKey: ['cab-workbench-meetings'] });
    },
  });

  const handleChange = (updates: Partial<CabAgendaItemFormData>) => {
    if (!formData) return;
    setFormData({ ...formData, ...updates });
    const keys = Object.keys(updates) as Array<keyof CabAgendaItemFormData>;
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const handleChangeSelected = (change: LookupChangeRequest) => {
    handleChange({
      changeRequestId: change.id,
      changeNumber: change.number,
      shortDescription: change.shortDescription || '',
    });
  };

  const meetingAgendaPath = `/admin/cab-meeting-management/cab-meetings/${meetingId}?tab=agenda`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData || !meetingId || !agendaItemId) return;

    const validationErrors = validateCabAgendaItemForm(formData);
    if (hasAgendaValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      toast.error('Please fix the validation errors before submitting.');
      return;
    }

    try {
      await saveMutation.mutateAsync({
        meetingName: formData.meetingName.trim(),
        changeRequestId: formData.changeRequestId,
        shortDescription: formData.shortDescription.trim(),
        status: formData.status,
        sortOrder: Number(formData.sortOrder),
      });
      toast.success('Agenda Item updated successfully.');
      navigate(meetingAgendaPath);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to update Agenda Item. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Delete this Agenda Item? Other agenda items for this meeting will not be changed.'
      )
    ) {
      return;
    }

    try {
      await deleteMutation.mutateAsync();
      toast.success('Agenda Item deleted successfully.');
      navigate(meetingAgendaPath);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to delete Agenda Item. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(meetingAgendaPath);
  };

  if (meetingLoading || itemLoading || !formData) {
    return <div className="p-6 text-gray-600">Loading...</div>;
  }

  if (!meeting || itemError || !agendaItem) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-2">CAB Agenda Item</h1>
        <p className="text-gray-600 mb-4">Agenda Item not found.</p>
        <button
          type="button"
          onClick={() => navigate(meetingId ? meetingAgendaPath : '/admin/cab-meeting-management/cab-definitions')}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>
    );
  }

  const isBusy = saveMutation.isPending || deleteMutation.isPending;

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-auto">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to="/admin/cab-meeting-management/cab-definitions"
              className="hover:text-indigo-600"
            >
              CAB Meeting Management
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to={`/admin/cab-meeting-management/cab-definitions/${meeting.cabDefinitionId}?tab=meetings`}
              className="hover:text-indigo-600"
            >
              {meeting.cabDefinitionName}
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to={meetingAgendaPath}
              className="hover:text-indigo-600 truncate max-w-xs inline-block align-bottom"
            >
              {meeting.name}
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium">{agendaItem.changeNumber}</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">CAB Agenda Item</h1>
          <p className="text-sm text-gray-600 mt-1">{agendaItem.changeNumber}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
            <div className="space-y-4">
              <div className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                  <label htmlFor="agenda-meeting" className="text-sm font-medium text-gray-700">
                    <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                    Meeting
                  </label>
                </div>
                <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                  <input
                    id="agenda-meeting"
                    type="text"
                    value={formData.meetingName}
                    maxLength={255}
                    onChange={(e) => handleChange({ meetingName: e.target.value })}
                    className={`w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                      errors.meetingName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.meetingName && (
                    <p className="mt-1 text-xs text-red-600">{errors.meetingName}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                  <label htmlFor="agenda-task" className="text-sm font-medium text-gray-700">
                    <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                    Task
                  </label>
                </div>
                <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                  <ChangeRequestTaskField
                    changeRequestId={formData.changeRequestId}
                    changeNumber={formData.changeNumber}
                    error={errors.changeNumber}
                    onSelect={handleChangeSelected}
                    onClear={() =>
                      handleChange({
                        changeRequestId: '',
                        changeNumber: '',
                        shortDescription: '',
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                  <label htmlFor="agenda-order" className="text-sm font-medium text-gray-700">
                    <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                    Order
                  </label>
                </div>
                <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                  <input
                    id="agenda-order"
                    type="number"
                    min={1}
                    step={1}
                    value={formData.sortOrder}
                    onChange={(e) =>
                      handleChange({
                        sortOrder: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                    className={`w-28 px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                      errors.sortOrder ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.sortOrder && (
                    <p className="mt-1 text-xs text-red-600">{errors.sortOrder}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                  <label htmlFor="agenda-status" className="text-sm font-medium text-gray-700">
                    <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                    Status
                  </label>
                </div>
                <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                  <select
                    id="agenda-status"
                    value={formData.status}
                    onChange={(e) =>
                      handleChange({
                        status: e.target.value as CabAgendaItemFormData['status'],
                      })
                    }
                    className={`w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                      errors.status ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {CAB_AGENDA_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-xs text-red-600">{errors.status}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3 items-start pt-2">
            <div className="col-span-12 sm:col-span-2 lg:col-span-1 pt-2 text-right sm:pr-2">
              <label htmlFor="agenda-short-description" className="text-sm font-medium text-gray-700">
                <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                Short Description
              </label>
            </div>
            <div className="col-span-12 sm:col-span-10 lg:col-span-11">
              <textarea
                id="agenda-short-description"
                rows={4}
                maxLength={CAB_AGENDA_SHORT_DESCRIPTION_MAX}
                value={formData.shortDescription}
                onChange={(e) => handleChange({ shortDescription: e.target.value })}
                className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                  errors.shortDescription ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <div className="mt-1 flex items-center justify-between gap-2">
                {errors.shortDescription ? (
                  <p className="text-xs text-red-600">{errors.shortDescription}</p>
                ) : (
                  <span />
                )}
                <p className="text-xs text-gray-500">
                  {formData.shortDescription.length}/{CAB_AGENDA_SHORT_DESCRIPTION_MAX}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isBusy}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Updating...' : 'Update'}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isBusy}
              className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isBusy}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CabAgendaItemEdit;
